import { get } from 'svelte/store';
import { locale, _ } from 'svelte-i18n';
import { logger } from './logger';
import type { Currency } from '../stores/meetingStore';
import { formatCurrency } from './formatters';
import { i18nStore } from '../stores/i18nStore';

/**
 * Check if browser notifications are supported
 */
export function isNotificationSupported(): boolean {
  if (typeof window === 'undefined') return false;
  return 'Notification' in window;
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission(): NotificationPermission {
  if (!isNotificationSupported()) {
    return 'denied';
  }
  return Notification.permission;
}

/**
 * Request notification permission from the user
 * @returns Promise that resolves to the permission status
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!isNotificationSupported()) {
    logger.warn('Notifications are not supported in this browser');
    return 'denied';
  }

  // If already granted or denied, return current status
  if (Notification.permission !== 'default') {
    return Notification.permission;
  }

  try {
    const permission = await Notification.requestPermission();
    logger.debug('Notification permission requested', { permission });
    return permission;
  } catch (error) {
    logger.error('Error requesting notification permission', error);
    return 'denied';
  }
}

/**
 * Check if notifications can be shown (supported, enabled, and permission granted)
 */
export function canShowNotifications(enabled: boolean): boolean {
  if (!enabled) return false;
  if (!isNotificationSupported()) return false;
  return getNotificationPermission() === 'granted';
}

/**
 * Get favicon path based on color
 */
function getFaviconPath(color: 'green' | 'yellow' | 'red'): string {
  const filename = color === 'green' ? 'favicon.svg' : `favicon-${color}.svg`;
  return `/${filename}`;
}

/**
 * Get translated string from i18n with fallback
 */
function getTranslation(key: string): string {
  try {
    // Check if i18n is initialized
    if (typeof get === 'function' && typeof _ !== 'undefined') {
      const translated = get(_)(key);
      // If translation returns the key itself, it means translation wasn't found
      // In that case, use fallback
      if (translated && translated !== key) {
        return translated;
      }
    }
  } catch {
    // Fall through to fallback
  }
  
  // Fallback translations (English)
  const fallbacks: Record<string, string> = {
    'notifications.yellowThreshold.title': 'Meeting Cost Counter - Yellow Threshold Reached',
    'notifications.yellowThreshold.body': 'Cost has reached the yellow threshold: {cost}',
    'notifications.redThreshold.title': 'Meeting Cost Counter - Red Threshold Reached',
    'notifications.redThreshold.body': 'Cost has reached the red threshold: {cost}',
  };
  
  return fallbacks[key] || key;
}

/**
 * Show a threshold notification
 * @param thresholdType 'yellow' or 'red'
 * @param cost Current meeting cost
 * @param currency Currency code
 */
export async function showThresholdNotification(
  thresholdType: 'yellow' | 'red',
  cost: number,
  currency: Currency
): Promise<void> {
  if (!isNotificationSupported()) {
    logger.warn('Notifications are not supported');
    return;
  }

  if (getNotificationPermission() !== 'granted') {
    logger.warn('Notification permission not granted');
    return;
  }

  try {
    // Get current language for formatting
    const currentLanguage = get(i18nStore);
    
    // Format the cost
    const formattedCost = formatCurrency(cost, currency, currentLanguage);
    
    // Get localized strings
    const titleKey = `notifications.${thresholdType}Threshold.title`;
    const bodyKey = `notifications.${thresholdType}Threshold.body`;
    
    // Get translated title and body
    let title = getTranslation(titleKey);
    let body = getTranslation(bodyKey);
    
    // Replace {cost} placeholder in body
    body = body.replace('{cost}', formattedCost);

    // Create notification options
    const icon = getFaviconPath(thresholdType === 'yellow' ? 'yellow' : 'red');
    const tag = `threshold-${thresholdType}`;

    const notification = new Notification(title, {
      body,
      icon,
      tag,
      badge: icon,
      requireInteraction: false,
    });

    logger.debug('Notification shown', { thresholdType, cost, currency, tag, title, body });

    // Auto-close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);
  } catch (error) {
    logger.error('Error showing notification', error);
  }
}
